import {
  CUSTOM_REPORT_CONFIGURATION_SETTINGS,
  EQUIPMENT_EMISSION_CUSTOM_REPORT_GROUP_BY,
  EQUIPMENT_RECORD_CUSTOM_REPORT_GROUP_BY,
  FACILITY_RECORD_CUSTOM_REPORT_GROUP_BY,
  FLOW_RECORD_CUSTOM_REPORT_GROUP_BY,
  apiKeyToFilterKey,
  parseCustomReportConfiguration,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import { useListCalculatorResultSavedFilters } from "#hooks/adapters/useCalculatorResults";
import { useListRecordSavedFilters } from "#hooks/adapters/useRecords";
import { CALCULATOR_FILTERS } from "#hooks/filters/useCalculatorFilters";
import { EQUIPMENT_FILTERS } from "#hooks/filters/useEquipmentFilters";
import { FLOW_FILTERS } from "#hooks/filters/useFlowFilters";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { getIntervalInMonthsFormatted, hydrateDateRange } from "#utils/date";
import {
  AssetType,
  CalculatorResultGetListRequestSchema,
  FacilitySchema,
  RecordGetListRequestSchema,
  SavedFilterType,
  getSchemaPropertyReference,
  type AssetTypeType,
  type FilterObjectType,
} from "@validereinc/domain";
import { toFlattenedObject, yearMonthParser } from "@validereinc/utilities";
import { uniq } from "es-toolkit/compat";

const formatStatusFilter = (status: string | string[]) =>
  Array.isArray(status) ? status : { $exact: status };

export const CustomReportVariants = {
  EMISSIONS: "emissions",
  RECORDS: "records",
} as const;
export type CustomReportVariantType =
  (typeof CustomReportVariants)[keyof typeof CustomReportVariants];

export const useCustomReportUtils = (
  variant: CustomReportVariantType,
  assetType: AssetTypeType
) => {
  const { measurementTypes } = useMeasurementTypes();

  const reportUtils = {
    [CustomReportVariants.EMISSIONS]: {
      measurementTypeFilter: (measurementTypeId: string) =>
        measurementTypes.find(({ id }) => id === measurementTypeId)
          ?.quantity !== "volume",
    },
    [CustomReportVariants.RECORDS]: {
      measurementTypeFilter: (measurementTypeId: string) =>
        assetType === AssetType.FLOW
          ? measurementTypes.find(({ id }) => id === measurementTypeId)
              ?.quantity === "volume"
          : true,
    },
  };
  return reportUtils[variant];
};

/** FilterPanel + ConfigurationPanel => API Filters */
export const getCustomReportFilters = (
  variant: CustomReportVariantType,
  assetType: AssetTypeType,
  config: Record<string, any>,
  filters: Record<string, any>
) => {
  const { reportingGroup, dateRange, outputs } =
    parseCustomReportConfiguration(config);

  const {
    [EQUIPMENT_FILTERS.equipmentName.name]: equipmentId,
    [EQUIPMENT_FILTERS.equipmentType.name]: equipmentTypeId,
    [EQUIPMENT_FILTERS.equipmentStatus.name]: equipmentStatus,
    [EQUIPMENT_FILTERS.facilityName.name]: equipmentFacilityName,
    [EQUIPMENT_FILTERS.facilityStatus.name]: equipmentFacilityStatus,
    [CALCULATOR_FILTERS.estimationMethod.name]: estimationMethod,
    [CALCULATOR_FILTERS.calculator.name]: calculator,
    [FLOW_FILTERS.flowType.name]: flowTypeId,
    [FLOW_FILTERS.flowStatus.name]: flowStatus,
    [FLOW_FILTERS.flowProductType.name]: flowProductType,
    [FLOW_FILTERS.flowProductCategory.name]: flowProductCategory,
    [FLOW_FILTERS.flowAssociatedFacility.name]: flowAssociatedFacility,
    [FLOW_FILTERS.flowAssociatedEquipment.name]: flowAssociatedEquipment,
    [getSchemaPropertyReference(FacilitySchema, "name", ["facility"])]:
      facilityName,
    [getSchemaPropertyReference(FacilitySchema, "status", ["facility"])]:
      facilityStatus,
    reporting_group: _,
    reporting_group_id: __,
    asset_type: ___,
    ...customAttributeFilters
  } = filters;

  const emissionsFilters = {
    ...(reportingGroup ? { reporting_group: { $exact: reportingGroup } } : {}),
    ...(equipmentTypeId ? { ["equipment.type.id"]: equipmentTypeId } : {}),
    ...(equipmentStatus
      ? {
          ["equipment.status"]: formatStatusFilter(equipmentStatus),
        }
      : {}),
    ...(equipmentFacilityStatus
      ? {
          ["equipment.facility.status"]: formatStatusFilter(
            equipmentFacilityStatus
          ),
        }
      : {}),
    ...(equipmentFacilityName
      ? { ["equipment.facility.id"]: equipmentFacilityName }
      : {}),
    ...(equipmentId ? { ["equipment.id"]: equipmentId } : {}),
    ...(estimationMethod
      ? { ["estimation_method.id"]: formatStatusFilter(estimationMethod) }
      : {}),
    ...(calculator
      ? {
          ["estimation_method.analytics_calculator_id"]:
            formatStatusFilter(calculator),
        }
      : {}),
  };

  const recordsFilters = {
    asset_type: { $exact: assetType },
    ...(reportingGroup
      ? { reporting_group_id: { $exact: reportingGroup } }
      : {}),
    ...(flowTypeId ? { ["flow.type"]: flowTypeId } : {}),
    ...(flowStatus ? { ["flow.status"]: formatStatusFilter(flowStatus) } : {}),
    ...(flowProductType ? { ["flow.product_type"]: flowProductType } : {}),
    ...(flowProductCategory
      ? { ["flow.product_category"]: flowProductCategory }
      : {}),
    ...(flowAssociatedFacility
      ? { ["flow.associated_facility_id"]: flowAssociatedFacility }
      : {}),
    ...(flowAssociatedEquipment
      ? { ["flow.associated_equipment_id"]: flowAssociatedEquipment }
      : {}),
    ...(facilityName ? { ["facility.id"]: facilityName } : {}),
    ...(facilityStatus
      ? { ["facility.status"]: formatStatusFilter(facilityStatus) }
      : {}),
    ...(equipmentTypeId ? { ["equipment.type.id"]: equipmentTypeId } : {}),
    ...(equipmentStatus
      ? {
          ["equipment.status"]: formatStatusFilter(equipmentStatus),
        }
      : {}),
    ...(equipmentFacilityStatus
      ? {
          ["equipment.facility.status"]: formatStatusFilter(
            equipmentFacilityStatus
          ),
        }
      : {}),
    ...(equipmentFacilityName
      ? { ["equipment.facility.id"]: equipmentFacilityName }
      : {}),
    ...(equipmentId ? { ["equipment.id"]: equipmentId } : {}),
  };

  return {
    ...(outputs ? { measurement_type: outputs } : {}),
    ...(dateRange?.from && dateRange?.to
      ? {
          year_month: getIntervalInMonthsFormatted(
            hydrateDateRange(dateRange as any)
          ),
        }
      : {}),
    ...(variant === CustomReportVariants.EMISSIONS ? emissionsFilters : {}),
    ...(variant === CustomReportVariants.RECORDS ? recordsFilters : {}),
    ...toFlattenedObject(customAttributeFilters),
  };
};

export const getRowsFromGroupBy = (
  variant: CustomReportVariantType,
  assetType: AssetTypeType,
  groupBy: string[] = []
) => {
  let groupByMap: Record<string, string[]> = {};
  switch (variant) {
    case CustomReportVariants.EMISSIONS:
      groupByMap = EQUIPMENT_EMISSION_CUSTOM_REPORT_GROUP_BY;
      break;
    case CustomReportVariants.RECORDS:
      groupByMap =
        assetType === AssetType.FACILITY
          ? FACILITY_RECORD_CUSTOM_REPORT_GROUP_BY
          : assetType === AssetType.EQUIPMENT
            ? EQUIPMENT_RECORD_CUSTOM_REPORT_GROUP_BY
            : assetType === AssetType.FLOW
              ? FLOW_RECORD_CUSTOM_REPORT_GROUP_BY
              : {};
      break;
    default:
  }
  const rows: string[] = [];
  let remainingGroupBy = [...groupBy];
  while (remainingGroupBy.length) {
    if (remainingGroupBy[0]?.includes("custom_attributes")) {
      rows.push(remainingGroupBy.shift()!);
    } else if (
      !Object.entries(groupByMap).some(([row, rowGroupBy]) => {
        const targetEntries = remainingGroupBy.slice(0, rowGroupBy.length);
        if (rowGroupBy.join(",") === targetEntries.join(",")) {
          rows.push(row);
          remainingGroupBy = remainingGroupBy.slice(rowGroupBy.length);
          return true;
        }
        return false;
      })
    ) {
      remainingGroupBy = remainingGroupBy.slice(1);
    }
  }
  return rows;
};

/** Get the FilterPanel value from a set of API filters */
const reverseFilter = (filter: FilterObjectType<unknown>) => {
  return Object.fromEntries(
    Object.entries(filter).map(([key, value]) => {
      const mappedKey = apiKeyToFilterKey[key] ?? key;
      if (value && typeof value === "object" && "$in" in value && value?.$in) {
        return [mappedKey, value.$in];
      }
      if (
        value &&
        typeof value === "object" &&
        "$like" in value &&
        value?.$like
      ) {
        return [mappedKey, value.$like];
      }
      if (
        value &&
        typeof value === "object" &&
        "$exact" in value &&
        value?.$exact
      ) {
        return [mappedKey, value.$exact];
      }

      return [mappedKey, value];
    })
  );
};

export const getCustomReportGroupBy = (
  variant: CustomReportVariantType,
  assetType: AssetTypeType,
  rows: string[] = [],
  /** if this is true, we only return the "display" groupBy item */
  isExport = false
) => {
  let groupByMap: Record<string, string[]> = {};
  switch (variant) {
    case CustomReportVariants.EMISSIONS:
      groupByMap = EQUIPMENT_EMISSION_CUSTOM_REPORT_GROUP_BY;
      break;
    case CustomReportVariants.RECORDS:
      groupByMap =
        assetType === AssetType.FACILITY
          ? FACILITY_RECORD_CUSTOM_REPORT_GROUP_BY
          : assetType === AssetType.EQUIPMENT
            ? EQUIPMENT_RECORD_CUSTOM_REPORT_GROUP_BY
            : assetType === AssetType.FLOW
              ? FLOW_RECORD_CUSTOM_REPORT_GROUP_BY
              : {};
      break;
    default:
  }

  return uniq(
    rows
      .filter((row) => row.startsWith(assetType) || groupByMap[row])
      .flatMap((row) => {
        if (!isExport) {
          // for non-exports, we use all the group by columns specified for the selected row
          return groupByMap[row] ?? [row];
        }

        // only use the last group by column for exports
        const groupColumn = groupByMap[row]?.[groupByMap[row].length - 1];
        return [groupColumn ?? row];
      })
  );
};

const CalculatorResultGetListFilterSchema =
  CalculatorResultGetListRequestSchema.shape.filters.unwrap();
const RecordGetListFilterSchema =
  RecordGetListRequestSchema.shape.filters.unwrap();

/** Get the ConfigurationPanel value from a saved filter */
export const getConfigFromSavedFilter = (
  variant: CustomReportVariantType,
  savedFilter: SavedFilterType<any>
) => {
  if (!savedFilter?.filter) {
    return {};
  }
  const {
    filter: {
      // available in both calculator and record filters
      year_month,
      // available just in calculator result filters
      reporting_group,
      // available just in calculator result filters
      measurement_type,
      // rest are applicable to either set of filters
      ...remainingFilters
    },
    group_by,
  } = savedFilter as SavedFilterType<
    typeof CalculatorResultGetListFilterSchema
  >;
  const {
    filter: { reporting_group_id, asset_type },
  } = savedFilter as unknown as SavedFilterType<
    typeof RecordGetListFilterSchema
  >;

  const sanitizedReportingGroup = reverseFilter({
    // @ts-expect-error TODO: Typing on filter
    reporting_group,
    reporting_group_id,
  });
  const sanitizedAssetType = reverseFilter({
    // @ts-expect-error TODO: typing on filter
    asset_type,
  });

  const reportingGroupId =
    sanitizedReportingGroup.reporting_group_id ??
    sanitizedReportingGroup.reporting_group;

  return {
    config: {
      ...(variant === CustomReportVariants.RECORDS
        ? {
            // flow is the default for backwards compatibility
            [CUSTOM_REPORT_CONFIGURATION_SETTINGS.assetType.name]:
              sanitizedAssetType.asset_type ?? AssetType.FLOW,
          }
        : {}),
      [CUSTOM_REPORT_CONFIGURATION_SETTINGS.reportingScenario.name]:
        reportingGroupId,
      [CUSTOM_REPORT_CONFIGURATION_SETTINGS.dateRange.name]: (year_month as any)
        ?.$in
        ? {
            from: yearMonthParser((year_month as any).$in[0]),
            to: yearMonthParser(
              (year_month as any).$in[(year_month as any).$in.length - 1]
            ),
          }
        : undefined,
      [CUSTOM_REPORT_CONFIGURATION_SETTINGS.rows.name]: getRowsFromGroupBy(
        variant,
        (sanitizedAssetType.asset_type as AssetTypeType) ?? AssetType.FLOW,
        group_by ?? []
      ),
      [CUSTOM_REPORT_CONFIGURATION_SETTINGS.outputs.name]:
        (measurement_type as any)?.$in ?? [],
    },
    filter: reverseFilter(remainingFilters),
  };
};

export const useSavedCustomReport = (
  reportVariant: CustomReportVariantType,
  reportId?: string
) => {
  const resultFiltersQuery = useListCalculatorResultSavedFilters(
    {},
    {
      enabled: reportVariant === CustomReportVariants.EMISSIONS,
    }
  );

  const recordFiltersQuery = useListRecordSavedFilters(
    {},
    {
      enabled: reportVariant === CustomReportVariants.RECORDS,
    }
  );

  let savedFilter;

  switch (reportVariant) {
    case CustomReportVariants.EMISSIONS:
      savedFilter = resultFiltersQuery.data?.data?.find(
        ({ id }) => id === reportId
      );
      break;
    case CustomReportVariants.RECORDS:
      savedFilter = recordFiltersQuery.data?.data?.find(
        ({ id }) => id === reportId
      );
      break;
  }

  return {
    data: savedFilter,
    query:
      reportVariant === CustomReportVariants.EMISSIONS
        ? resultFiltersQuery
        : reportVariant === CustomReportVariants.RECORDS
          ? recordFiltersQuery
          : {
              isLoading: false,
            },
  };
};
